import React, { createContext, useState, useEffect, useContext } from 'react';

const ThemeContext = createContext();

/**
 * ThemeProvider
 * 
 * Provides theme context with light/dark mode support
 * Theme preference is persisted to localStorage
 * 
 * @example
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 */
export const ThemeProvider = ({ children }) => {
  // Default to 'light' theme
  const [theme, setTheme] = useState('light');
  const [isHydrated, setIsHydrated] = useState(false);

  // Load theme from localStorage or OS preference on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    
    if (savedTheme === 'dark' || savedTheme === 'light') {
      // User has explicit preference
      setTheme(savedTheme);
    } else {
      // Check OS preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setTheme(prefersDark ? 'dark' : 'light');
    }
    
    setIsHydrated(true);
  }, []);

  // Save theme to localStorage when it changes
  useEffect(() => {
    if (isHydrated) {
      localStorage.setItem('theme', theme);
      // Apply theme class to document root
      document.documentElement.classList.remove('light', 'dark');
      document.documentElement.classList.add(theme);
      // Also set data attribute for easier CSS selection
      document.documentElement.setAttribute('data-theme', theme);
    }
  }, [theme, isHydrated]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const setLightTheme = () => setTheme('light');
  const setDarkTheme = () => setTheme('dark');

  const value = {
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    isHydrated,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * useTheme Hook
 * 
 * Access theme context
 * 
 * @returns {Object} Theme context value
 * 
 * @example
 * const { theme, isDark, toggleTheme } = useTheme();
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
